import { Link } from "react-router-dom";
import { ArrowRight, Swords, Anchor, Castle, Sparkles, ScrollText } from "lucide-react";
import { TIMELINE, BATTLES, TREATIES, FORTS, PIRATES, COCOA, FACTS } from "@/data/history";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { SectionTitle } from "@/components/SectionTitle";
import { Seo } from "@/components/Seo";
import { VisitBand } from "@/components/TobagoHistory";

const SECTIONS = [
  ["timeline", "Timeline"],
  ["battles", "Battles & Sieges"],
  ["treaties", "Treaties"],
  ["pirates", "Pirates & Privateers"],
  ["forts", "Forts"],
  ["cocoa", "Cocoa & Trade"],
  ["facts", "Did You Know?"],
];

const Chips = () => (
  <nav className="sticky top-[72px] z-30 border-b border-gold/30 bg-ivory/95 backdrop-blur-md lg:top-[88px]" aria-label="History sections" data-testid="history-section-nav">
    <div className="mx-auto flex max-w-[1440px] gap-2 overflow-x-auto px-5 py-3 md:px-10 lg:justify-center">
      {SECTIONS.map(([id, label]) => (
        <a key={id} href={`#${id}`} data-testid={`history-chip-${id}`} className="shrink-0 rounded-full border border-gold/50 px-4 py-2 font-caps text-[11px] tracking-[0.16em] uppercase text-heritage transition-colors hover:bg-heritage hover:text-ivory">
          {label}
        </a>
      ))}
    </div>
  </nav>
);

const Block = ({ id, eyebrow, title, intro, dark, children }) => (
  <section id={id} className={`scroll-mt-40 py-20 lg:py-24 ${dark ? "bg-heritage text-ivory" : "odd:bg-ivory even:bg-ivory-muted"}`} data-testid={`history-${id}`}>
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <FadeUp><SectionTitle eyebrow={eyebrow} title={title} light={dark}>{intro}</SectionTitle></FadeUp>
      <div className="mt-12">{children}</div>
    </div>
  </section>
);

const Card = ({ icon: Icon, kicker, title, sub, text, i, dark }) => (
  <FadeUp delay={(i % 3) * 0.06} className={`h-full border p-7 ${dark ? "border-gold/30 bg-heritage-deep/40" : "border-gold/40 bg-ivory"}`}>
    {Icon && <Icon size={20} className="text-gold" />}
    {kicker && <p className={`mt-3 font-caps text-[11px] tracking-[0.22em] uppercase ${dark ? "text-gold" : "text-gold-deep"}`}>{kicker}</p>}
    <h3 className={`mt-2 font-display text-2xl leading-snug ${dark ? "text-ivory" : "text-heritage"}`}>{title}</h3>
    {sub && <p className={`mt-1 text-sm italic ${dark ? "text-ivory/70" : "text-muted-ink"}`}>{sub}</p>}
    <p className={`mt-3 text-[17px] leading-relaxed ${dark ? "text-ivory/85" : "text-muted-ink"}`}>{text}</p>
  </FadeUp>
);

const Timeline = () => (
  <ol className="mx-auto max-w-4xl">
    {TIMELINE.map((t, i) => (
      <FadeUp key={t.year + t.title} y={16}>
        <li className="relative grid gap-1 border-l border-gold/40 pb-10 pl-8 md:grid-cols-[12rem_1fr] md:gap-8 md:pl-10" data-testid={`timeline-entry-${i}`}>
          <span className={`absolute -left-[7px] top-2 h-3.5 w-3.5 rotate-45 border border-gold ${t.year === "1777" ? "bg-gold" : "bg-ivory"}`} />
          <p className="font-caps text-sm tracking-[0.14em] text-gold-deep">{t.year}</p>
          <div>
            <h3 className="font-display text-2xl leading-tight text-heritage md:text-3xl">{t.title}</h3>
            <p className="mt-2 text-lg leading-relaxed text-muted-ink">{t.text}</p>
          </div>
        </li>
      </FadeUp>
    ))}
  </ol>
);

export default function History() {
  return (
    <>
      <Seo title="The History of Tobago" path="/history" description="A knowledge base on Tobago's history: more than 30 changes of flag, the battles of 1677 and 1781, pirates and privateers, colonial forts, cocoa and trade, and surprising facts." />
      <PageHero eyebrow="Knowledge Base · A History of Tobago" lines={["The Island", "Everyone Wanted"]} image={{ src: "/images/fort.webp" }} alt="Old cannon on a colonial fort overlooking a Caribbean bay" testid="history-hero" />
      <Chips />

      <Block id="timeline" eyebrow="Chronology" title="Five Centuries at a Glance" intro="From the first island peoples to independence: the dates that shaped Tobago, and the estate's own year, 1777, in the middle of them.">
        <Timeline />
      </Block>

      <Block id="battles" eyebrow="Battles & Sieges" title="Fought Over, Again and Again" intro="Fleets, sieges and invasions decided who held Tobago. These are the engagements that changed its flag." dark>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {BATTLES.map((b, i) => <Card key={b.date + b.name} icon={Swords} kicker={b.date} title={b.name} sub={b.sides} text={b.text} i={i} dark />)}
        </div>
      </Block>

      <Block id="treaties" eyebrow="Diplomacy" title="Won and Lost at the Treaty Table" intro="Just as often, Tobago changed hands in European palaces, through treaties signed by people who had never seen it.">
        <div className="mx-auto max-w-4xl border-t border-gold/40">
          {TREATIES.map(([y, n, r]) => (
            <FadeUp key={y + n} y={12}>
              <div className="grid grid-cols-[4.5rem_1fr] items-baseline gap-4 border-b border-gold/30 py-5 sm:grid-cols-[5rem_1fr_auto]" data-testid={`treaty-${y}`}>
                <span className="font-display text-3xl text-gold-deep">{y}</span>
                <span className="flex items-center gap-3 font-display text-2xl text-heritage"><ScrollText size={18} className="shrink-0 text-gold" />{n}</span>
                <span className="col-start-2 font-caps text-[12px] tracking-[0.16em] uppercase text-muted-ink sm:col-start-auto">{r}</span>
              </div>
            </FadeUp>
          ))}
        </div>
      </Block>

      <Block id="pirates" eyebrow="Pirates, Privateers & Castaways" title="Where the Law Did Not Reach" intro="With no nation in charge for much of its history, Tobago's coves drew every kind of seafarer.">
        <div className="grid gap-5 md:grid-cols-2">
          {PIRATES.map((p, i) => <Card key={p.title} icon={Anchor} title={p.title} text={p.text} i={i} />)}
        </div>
      </Block>

      <Block id="forts" eyebrow="Stone & Cannon" title="The Forts of Tobago" intro="Most of Tobago's surviving forts date from the wars of the late 1700s. Two of the best known were begun in 1777, the year the estate was established.">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FORTS.map((f, i) => <Card key={f.name} icon={Castle} kicker={f.place} title={f.name} text={f.text} i={i} />)}
        </div>
      </Block>

      <Block id="cocoa" eyebrow="Cocoa & Trade" title="Sugar, Cocoa and the Trade Winds" intro="What grew on the island, and where its ships sailed, explains why so many empires wanted it." dark>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {COCOA.map((c, i) => <Card key={c.title} title={c.title} text={c.text} i={i} dark />)}
        </div>
      </Block>

      <Block id="facts" eyebrow="Did You Know?" title="Things Most Visitors Never Hear" intro="Surprising stories from Tobago's past and its natural world.">
        <div className="grid gap-px bg-gold/30 sm:grid-cols-2 lg:grid-cols-3">
          {FACTS.map((f, i) => (
            <FadeUp key={i} delay={(i % 3) * 0.05} className="bg-ivory p-7">
              <div className="flex gap-4" data-testid={`fact-${i}`}>
                <Sparkles size={18} className="mt-1 shrink-0 text-gold" />
                <p className="text-[17px] leading-relaxed text-heritage">{f}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-14 text-center">
          <Link to="/estate" data-testid="history-estate-link" className="group inline-flex items-center gap-3 border border-gold-deep bg-gold-deep px-7 py-4 font-caps text-[12px] tracking-[0.24em] uppercase text-ivory transition-colors duration-500 hover:border-heritage hover:bg-heritage">
            The Estate in This Story <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </Block>

      <VisitBand />
    </>
  );
}
